import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import {
  Card,
  FavoriteToggle,
  handlePageClick,
  Layout,
  ModalV2,
  Pagination,
  Tabs,
  toast,
} from '@utilities';
import { BookmarkIcon } from '@utilities/icons';
import {
  createVideoFavorite,
  deleteVideoFavorite,
  listVideoBookmarksSurvey,
  listVideoFavoritesSurvey,
} from '@api/instant_survey';

import VideoPlayer from './components/VideoPlayer';
import { formatTimestamp } from './utilities/helpers';

import styles from './_index.module.scss';

const PAGE_SIZE = 10;

const Video = ({ data, question, renderResponseTextSummary, surveyRef }) => {
  const [pagesByTab, setPagesByTab] = useState({});
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(null);
  const [bookmarks, setBookmarks] = useState([]);
  const [isLoadingBookmarks, setIsLoadingBookmarks] = useState(false);
  const [hasAttemptedFetchBookmarks, setHasAttemptedFetchBookmarks] = useState(false);
  const [expandedBookmarks, setExpandedBookmarks] = useState({});
  const [favorites, setFavorites] = useState([]);
  const [isLoadingFavorites, setIsLoadingFavorites] = useState(false);
  const [hasAttemptedFetchFavorites, setHasAttemptedFetchFavorites] = useState(false);
  const favoriteOverridesRef = useRef(new Map());
  const hasLoadedFavoritesRef = useRef(false);
  const pendingFavoriteResponseIdsRef = useRef(new Set());

  const segments = data?.segments || [];
  const [selectedTab, setSelectedTab] = useState('overall');

  // Reset state when surveyRef changes to prevent stale data
  useEffect(() => {
    setBookmarks([]);
    setFavorites([]);
    favoriteOverridesRef.current.clear();
    hasLoadedFavoritesRef.current = false;
    setHasAttemptedFetchBookmarks(false);
    setHasAttemptedFetchFavorites(false);
    pendingFavoriteResponseIdsRef.current.clear();
  }, [surveyRef]);

  // Fetch bookmarks when component mounts or surveyRef changes
  useEffect(() => {
    if (surveyRef && !hasAttemptedFetchBookmarks && !isLoadingBookmarks) {
      const abortController = new AbortController();
      setIsLoadingBookmarks(true);
      listVideoBookmarksSurvey({ signal: abortController.signal, surveyRef })
        .then((response) => {
          const results = response?.results || response || [];
          setBookmarks(results);
        })
        .catch((error) => {
          if (error.name === 'AbortError' || error.name === 'CanceledError') {
            return;
          }
          toast.error('Failed to load bookmarks');
          setBookmarks([]);
        })
        .finally(() => {
          if (!abortController.signal.aborted) {
            setIsLoadingBookmarks(false);
            setHasAttemptedFetchBookmarks(true);
          }
        });

      return () => {
        abortController.abort();
      };
    }
  }, [surveyRef, hasAttemptedFetchBookmarks]);

  // Fetch favorites when component mounts or surveyRef changes
  useEffect(() => {
    if (surveyRef && !hasAttemptedFetchFavorites && !isLoadingFavorites) {
      const abortController = new AbortController();
      setIsLoadingFavorites(true);
      listVideoFavoritesSurvey({ signal: abortController.signal, surveyRef })
        .then((response) => {
          let results = response?.results || response || [];

          favoriteOverridesRef.current.forEach((mutation) => {
            results = results.filter(
              (favorite) =>
                favorite.survey_response !== mutation.responseId ||
                favorite.question_id !== mutation.questionId
            );

            if (mutation.isFavorite) {
              results = [...results, mutation.favorite];
            }
          });

          setFavorites(results);
          hasLoadedFavoritesRef.current = true;
          favoriteOverridesRef.current.forEach((mutation, responseId) => {
            if (!mutation.isPending) {
              favoriteOverridesRef.current.delete(responseId);
            }
          });
        })
        .catch((error) => {
          // Ignore abort errors
          if (error.name === 'AbortError' || error.name === 'CanceledError') {
            return;
          }
          toast.error('Failed to load favorites');
          if (favoriteOverridesRef.current.size === 0) {
            setFavorites([]);
          }
        })
        .finally(() => {
          if (!abortController.signal.aborted) {
            setIsLoadingFavorites(false);
            setHasAttemptedFetchFavorites(true);
          }
        });

      return () => {
        abortController.abort();
      };
    }
  }, [surveyRef, hasAttemptedFetchFavorites]);

  // Refetch bookmarks when a bookmark is added or deleted
  const handleBookmarkChange = () => {
    if (surveyRef) {
      const abortController = new AbortController();
      setIsLoadingBookmarks(true);
      listVideoBookmarksSurvey({ signal: abortController.signal, surveyRef })
        .then((response) => {
          const results = response?.results || response || [];
          setBookmarks(results);
        })
        .catch((error) => {
          // Ignore abort errors
          if (error.name === 'AbortError' || error.name === 'CanceledError') {
            return;
          }
          toast.error('Failed to load bookmarks');
        })
        .finally(() => {
          if (!abortController.signal.aborted) {
            setIsLoadingBookmarks(false);
          }
        });
    }
  };

  // Handle toggle favorite (create or delete)
  const handleToggleFavorite = async (responseId) => {
    if (pendingFavoriteResponseIdsRef.current.has(responseId)) {
      return;
    }

    // Check if this response is already favorited
    const existingFavorite = favorites.find(
      (fav) => fav.survey_response === responseId && fav.question_id === question?.id
    );
    const optimisticFavorite = {
      id: `optimistic-${question?.id}-${responseId}`,
      question_id: question?.id,
      survey_response: responseId,
    };

    pendingFavoriteResponseIdsRef.current.add(responseId);
    favoriteOverridesRef.current.set(responseId, {
      favorite: optimisticFavorite,
      isFavorite: !existingFavorite,
      isPending: true,
      questionId: question?.id,
      responseId,
    });

    if (existingFavorite) {
      setFavorites((previousFavorites) =>
        previousFavorites.filter((favorite) => favorite.id !== existingFavorite.id)
      );
    } else {
      setFavorites((previousFavorites) => [...previousFavorites, optimisticFavorite]);
    }

    try {
      if (existingFavorite) {
        // Delete the favorite
        await deleteVideoFavorite({ favoriteId: existingFavorite.id });
      } else {
        // Create a new favorite
        const createdFavorite = await createVideoFavorite({
          questionId: question?.id,
          responseId,
        });

        favoriteOverridesRef.current.set(responseId, {
          favorite: createdFavorite,
          isFavorite: true,
          isPending: true,
          questionId: question?.id,
          responseId,
        });

        setFavorites((previousFavorites) => [
          ...previousFavorites.filter(
            (favorite) =>
              favorite.survey_response !== responseId || favorite.question_id !== question?.id
          ),
          createdFavorite,
        ]);
      }

      if (existingFavorite) {
        favoriteOverridesRef.current.set(responseId, {
          isFavorite: false,
          isPending: true,
          questionId: question?.id,
          responseId,
        });

        setFavorites((previousFavorites) =>
          previousFavorites.filter(
            (favorite) =>
              favorite.survey_response !== responseId || favorite.question_id !== question?.id
          )
        );
      }
    } catch (error) {
      if (existingFavorite) {
        favoriteOverridesRef.current.set(responseId, {
          favorite: existingFavorite,
          isFavorite: true,
          isPending: true,
          questionId: question?.id,
          responseId,
        });

        setFavorites((previousFavorites) => [
          ...previousFavorites.filter(
            (favorite) =>
              favorite.survey_response !== responseId || favorite.question_id !== question?.id
          ),
          existingFavorite,
        ]);
      } else {
        favoriteOverridesRef.current.set(responseId, {
          isFavorite: false,
          isPending: true,
          questionId: question?.id,
          responseId,
        });

        setFavorites((previousFavorites) =>
          previousFavorites.filter((favorite) => favorite.id !== optimisticFavorite.id)
        );
      }
      toast.error('Failed to toggle favorite');
    } finally {
      pendingFavoriteResponseIdsRef.current.delete(responseId);
      const override = favoriteOverridesRef.current.get(responseId);

      if (override) {
        override.isPending = false;
      }

      if (hasLoadedFavoritesRef.current) {
        favoriteOverridesRef.current.delete(responseId);
      }
    }
  };

  const overallSegment = useMemo(() => {
    if (selectedTab !== 'overall') {
      return null;
    }

    const allResponses = [];

    segments.forEach((segment) => {
      segment.responses?.forEach((response) => {
        allResponses.push(response);
      });
    });

    return {
      compareBy: 'Overall',
      name: 'Overall',
      responses: allResponses,
    };
  }, [selectedTab, segments]);

  // Create a bookmarks segment by grouping responses with bookmarks
  const bookmarksSegment = useMemo(() => {
    if (selectedTab !== 'bookmarks') {
      return null;
    }

    if (bookmarks.length === 0) {
      return {
        compareBy: 'Bookmarks',
        name: 'Bookmarks',
        responses: [],
      };
    }

    // Group bookmarks by response ID (filtered to current question only)
    const bookmarksByResponse = bookmarks.reduce((acc, bookmark) => {
      if (bookmark.question_id !== question.id) {
        return acc;
      }
      const responseId = bookmark.survey_response;
      if (!acc[responseId]) {
        acc[responseId] = [];
      }
      acc[responseId].push(bookmark);
      return acc;
    }, {});

    // Get all unique responses with bookmarks from all segments
    const responsesWithBookmarks = [];
    const seenResponseIds = new Set();

    segments.forEach((segment) => {
      segment.responses?.forEach((response) => {
        if (bookmarksByResponse[response.responseId] && !seenResponseIds.has(response.responseId)) {
          responsesWithBookmarks.push({
            ...response,
            bookmarks: bookmarksByResponse[response.responseId],
          });
          seenResponseIds.add(response.responseId);
        }
      });
    });

    return {
      compareBy: 'Bookmarks',
      name: 'Bookmarks',
      responses: responsesWithBookmarks,
    };
  }, [selectedTab, bookmarks, segments, question]);

  // Create a favorites segment by grouping responses with favorites
  const favoritesSegment = useMemo(() => {
    if (selectedTab !== 'favorites' || favorites.length === 0) {
      return null;
    }

    // Group bookmarks by response ID for showing in favorites tab (filtered to current question only)
    const bookmarksByResponse = bookmarks.reduce((acc, bookmark) => {
      if (bookmark.question_id !== question.id) {
        return acc;
      }
      const responseId = bookmark.survey_response;
      if (!acc[responseId]) {
        acc[responseId] = [];
      }
      acc[responseId].push(bookmark);
      return acc;
    }, {});

    // Create a map of favorites by response ID and question ID
    const favoritesByResponseAndQuestion = favorites.reduce((acc, favorite) => {
      const key = `${favorite.survey_response}_${favorite.question_id}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(favorite);
      return acc;
    }, {});

    // Get all unique responses with favorites from all segments
    const responsesWithFavorites = [];
    const seenResponseIds = new Set();

    segments.forEach((segment) => {
      segment.responses?.forEach((response) => {
        const key = `${response.responseId}_${question?.id}`;
        if (favoritesByResponseAndQuestion[key] && !seenResponseIds.has(response.responseId)) {
          responsesWithFavorites.push({
            ...response,
            bookmarks: bookmarksByResponse[response.responseId] || [],
            favorites: favoritesByResponseAndQuestion[key],
          });
          seenResponseIds.add(response.responseId);
        }
      });
    });

    return {
      compareBy: 'Favorites',
      name: 'Favorites',
      responses: responsesWithFavorites,
    };
  }, [selectedTab, favorites, bookmarks, segments, question]);

  const activeSegment =
    selectedTab === 'overall'
      ? overallSegment
      : selectedTab === 'bookmarks'
        ? bookmarksSegment
        : selectedTab === 'favorites'
          ? favoritesSegment
          : null;
  const currentPage = pagesByTab[selectedTab] ?? 1;

  const pagedResponses = useMemo(() => {
    const list = activeSegment?.responses ?? [];
    const start = (currentPage - 1) * PAGE_SIZE;
    return list.slice(start, start + PAGE_SIZE);
  }, [activeSegment, currentPage]);

  const totalResponses = activeSegment?.responses?.length ?? 0;
  const pageCount = Math.ceil(totalResponses / PAGE_SIZE);

  useEffect(() => {
    if (!segments.length) {
      setSelectedTab('overall');
      return;
    }

    setSelectedTab((prev) => {
      if (prev === 'overall' || prev === 'bookmarks' || prev === 'favorites') {
        return prev;
      }

      return 'overall';
    });
  }, [segments]);

  useEffect(() => {
    if (!selectedTab) return;
    setPagesByTab((prev) => ({
      ...prev,
      [selectedTab]: 1,
    }));
  }, [selectedTab]);

  const onPageChange = (event) => {
    const results = activeSegment?.responses ?? [];
    handlePageClick({
      event,
      items: results,
      itemsPerPage: PAGE_SIZE,
      setOffset: (offset) => {
        const nextPage = Math.floor(offset / PAGE_SIZE) + 1;
        setPagesByTab((prev) => ({
          ...prev,
          [selectedTab]: nextPage,
        }));
      },
    });
  };

  const handleClosePlayer = () => {
    setSelectedVideoIndex(null);
  };

  const allResponses = activeSegment?.responses ?? [];
  const selectedVideo = selectedVideoIndex !== null ? allResponses[selectedVideoIndex] : null;
  const hasPrevious = selectedVideoIndex > 0;
  const hasNext = selectedVideoIndex !== null && selectedVideoIndex < allResponses.length - 1;
  const isPlayerOpen = selectedVideoIndex !== null;

  const handlePreviousVideo = () => {
    setSelectedVideoIndex((prev) => (prev !== null && prev > 0 ? prev - 1 : prev));
  };

  const handleNextVideo = () => {
    setSelectedVideoIndex((prev) =>
      prev !== null && prev < allResponses.length - 1 ? prev + 1 : prev
    );
  };

  return (
    <>
      <Tabs
        onChange={setSelectedTab}
        options={[
          { label: 'Overall', value: 'overall' },
          { label: 'Bookmarks', value: 'bookmarks' },
          { label: 'Favorites', value: 'favorites' },
        ]}
        value={selectedTab}
      />
      {renderResponseTextSummary &&
        activeSegment &&
        renderResponseTextSummary({ question, segment: activeSegment })}
      {selectedTab === 'bookmarks' && totalResponses === 0 && (
        <Layout.Flex.Column
          className={styles['empty-state']}
          data-testid="video-bookmarks-empty-state"
        >
          <BookmarkIcon />
          <p>No bookmarks</p>
          <p>You can save bookmarks on individual videos</p>
        </Layout.Flex.Column>
      )}
      {selectedTab === 'favorites' && totalResponses === 0 && (
        <Layout.Flex.Column
          className={styles['empty-state']}
          data-testid="video-favorites-empty-state"
        >
          <BookmarkIcon />
          <p>No favorites</p>
          <p>You can save favorites on individual videos</p>
        </Layout.Flex.Column>
      )}
      {activeSegment && totalResponses > 0 && (
        <Layout.Flex.Column as="ul">
          {pagedResponses.map((response, pageIndex) => {
            const globalIndex = (currentPage - 1) * PAGE_SIZE + pageIndex;
            const responseBookmarks = response.bookmarks || [];
            const hasBookmarks = responseBookmarks.length > 0;
            const isExpanded = expandedBookmarks[response.responseId] || false;
            const visibleBookmarks = isExpanded ? responseBookmarks : responseBookmarks.slice(0, 3);
            const hasMoreBookmarks = responseBookmarks.length > 3;
            const isFavorited = favorites.some(
              (fav) =>
                fav.survey_response === response.responseId && fav.question_id === question?.id
            );

            return (
              <li key={response.responseId}>
                <Card
                  className={styles['video-card-response-container']}
                  onClick={() => {
                    setSelectedVideoIndex(globalIndex);
                  }}
                >
                  <Layout.Flex.Row className={styles['video-card-content']}>
                    {response.thumbnailUrl && (
                      <img
                        alt="Video thumbnail"
                        className={styles['video-card-thumbnail']}
                        src={response.thumbnailUrl}
                      />
                    )}
                    <div className={styles['video-card-summary']}>
                      <p className={styles['video-card-summary-text']}>{response.aiSummary}</p>
                      <p className={styles['video-ai-summary-text']}>
                        Summary generated by Numerator AI
                      </p>
                      {hasBookmarks && (
                        <div
                          className={styles['bookmarks-blurb']}
                          data-testid="video-card-bookmarks-blurb"
                        >
                          <div className={styles['bookmarks-header']}>
                            <BookmarkIcon />
                            <span className={styles['bookmarks-title']}>BOOKMARKS</span>
                          </div>
                          {visibleBookmarks.map((bookmark) => (
                            <div
                              className={styles['bookmark-item']}
                              data-testid="video-card-bookmark-item"
                              key={bookmark.id}
                            >
                              <span className={styles['bookmark-timestamp']}>
                                {formatTimestamp(bookmark.start_ts)}
                              </span>
                              <span className={styles['bookmark-text']}>{bookmark.text}</span>
                            </div>
                          ))}
                          {hasMoreBookmarks && !isExpanded && (
                            <button
                              className={styles['bookmark-show-more']}
                              data-testid="video-card-bookmark-show-more"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedBookmarks((prev) => ({
                                  ...prev,
                                  [response.responseId]: true,
                                }));
                              }}
                              type="button"
                            >
                              ...show more
                            </button>
                          )}
                        </div>
                      )}
                      <div
                        className={styles['favorite-toggle-wrapper']}
                        data-testid="video-card-favorite-toggle"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FavoriteToggle
                          isFavorite={isFavorited}
                          onChange={() => handleToggleFavorite(response.responseId)}
                        />
                      </div>
                    </div>
                  </Layout.Flex.Row>
                </Card>
              </li>
            );
          })}
          <Pagination onPageChange={onPageChange} pageCount={pageCount} show={pageCount > 1} />
        </Layout.Flex.Column>
      )}

      {selectedVideo && (
        <ModalV2 isActive={isPlayerOpen} onClose={handleClosePlayer} size="fullscreen">
          <VideoPlayer
            hasNext={hasNext}
            hasPrevious={hasPrevious}
            onBookmarkChange={handleBookmarkChange}
            onNext={handleNextVideo}
            onPrevious={handlePreviousVideo}
            questionId={question?.id}
            responseId={selectedVideo.responseId}
            videoData={{
              ai_summary: selectedVideo.aiSummary,
              audio_segments: selectedVideo.audioSegments,
              items: selectedVideo.items,
              video_url: selectedVideo.videoUrl,
            }}
          />
        </ModalV2>
      )}
    </>
  );
};

Video.propTypes = {
  data: PropTypes.object.isRequired,
  question: PropTypes.object,
  renderResponseTextSummary: PropTypes.func,
  surveyRef: PropTypes.number,
};

export default Video;
